/**
 * ExponentialBackoffStrategy
 *
 * Concrete implementation of IReconnectionStrategy
 * Exponential backoff with configurable delays
 *
 * Single Responsibility: Reconnection backoff calculation
 */

import type { IReconnectionStrategy, ReconnectionConfig, ReconnectionState } from '../interfaces';

export class ExponentialBackoffStrategy implements IReconnectionStrategy {
  private config: ReconnectionConfig;
  private state: ReconnectionState = {
    currentAttempt: 0,
    nextRetryDelayMs: 0,
    totalRetryTimeMs: 0,
    isExhausted: false,
  };

  constructor(config: Partial<ReconnectionConfig> = {}) {
    this.config = {
      strategy: 'exponential',
      initialDelayMs: 500,
      maxDelayMs: 60000,
      multiplier: 2,
      maxAttempts: 10,
      jitterFactor: 0.1,
      ...config,
    };
  }

  calculateBackoff(attemptNumber: number): number {
    if (this.isExhausted(attemptNumber)) {
      return 0;
    }

    const exponential = this.config.initialDelayMs * Math.pow(this.config.multiplier, attemptNumber - 1);
    const capped = Math.min(exponential, this.config.maxDelayMs);
    const withJitter = this.applyJitter(capped);

    this.state.nextRetryDelayMs = withJitter;
    return withJitter;
  }

  private applyJitter(delayMs: number): number {
    if (!this.config.jitterFactor || this.config.jitterFactor <= 0) {
      return delayMs;
    }
    const jitterAmount = delayMs * this.config.jitterFactor;
    const randomJitter = Math.random() * jitterAmount;
    return Math.ceil(delayMs + randomJitter);
  }

  isExhausted(attemptNumber: number): boolean {
    return attemptNumber > this.config.maxAttempts;
  }

  getMaxAttempts(): number {
    return this.config.maxAttempts;
  }

  getConfig(): ReconnectionConfig {
    return { ...this.config };
  }

  updateConfig(config: Partial<ReconnectionConfig>): void {
    if (config.maxAttempts !== undefined && config.maxAttempts < 1) {
      throw new Error('maxAttempts must be >= 1');
    }
    if (config.initialDelayMs !== undefined && config.initialDelayMs < 100) {
      throw new Error('initialDelayMs must be >= 100');
    }

    this.config = { ...this.config, ...config };
  }

  getState(): ReconnectionState {
    return { ...this.state };
  }

  reset(): void {
    this.state = {
      currentAttempt: 0,
      nextRetryDelayMs: 0,
      totalRetryTimeMs: 0,
      isExhausted: false,
    };
  }

  recordAttempt(_error?: Error): void {
    this.state.currentAttempt++;
    this.state.lastAttemptTime = new Date();
    this.state.totalRetryTimeMs += this.state.nextRetryDelayMs;
    this.state.isExhausted = this.isExhausted(this.state.currentAttempt);
  }

  getTotalRetryTime(): number {
    return this.state.totalRetryTimeMs;
  }

  isReadyForNextAttempt(): boolean {
    if (!this.state.lastAttemptTime) return true;
    const elapsedMs = Date.now() - this.state.lastAttemptTime.getTime();
    return elapsedMs >= this.state.nextRetryDelayMs;
  }

  getStatistics() {
    return {
      strategy: this.config.strategy,
      maxAttempts: this.config.maxAttempts,
      currentAttempt: this.state.currentAttempt,
      totalRetryTimeMs: this.state.totalRetryTimeMs,
      averageDelayMs: this.state.currentAttempt > 0 ? this.state.totalRetryTimeMs / this.state.currentAttempt : 0,
      minDelayMs: this.config.initialDelayMs,
      maxDelayMs: this.config.maxDelayMs,
      lastResetAt: undefined as Date | undefined,
    };
  }
}
